import { useState, useRef, useEffect } from 'react'
import { clsx } from 'clsx'
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline'

export interface SelectOption<T = string> {
  value: T
  label: string
  disabled?: boolean
}

interface SelectProps<T = string> {
  options: SelectOption<T>[]
  value?: T
  onChange: (value: T) => void
  placeholder?: string
  label?: string
  error?: string
  disabled?: boolean
  className?: string
  multiple?: boolean
  searchable?: boolean
}

export function Select<T = string>({
  options,
  value,
  onChange,
  placeholder = 'Sélectionner...',
  label,
  error,
  disabled = false,
  className,
  multiple = false,
  searchable = false,
}: SelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Filter options based on search query
  const filteredOptions = searchable
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options

  // Get selected option(s) for display
  const selectedOptions = options.filter(option => {
    if (multiple && Array.isArray(value)) {
      return value.includes(option.value)
    }
    return option.value === value
  })

  const displayText = selectedOptions.length > 0
    ? selectedOptions.map(option => option.label).join(', ')
    : placeholder

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen, searchable])

  const handleOptionSelect = (optionValue: T) => {
    if (multiple && Array.isArray(value)) {
      const currentValues = value as T[]
      const newValues = currentValues.includes(optionValue)
        ? currentValues.filter(v => v !== optionValue)
        : [...currentValues, optionValue]
      onChange(newValues as T)
    } else {
      onChange(optionValue)
      setIsOpen(false)
      setSearchQuery('')
    }
  }

  const isSelected = (optionValue: T) => {
    if (multiple && Array.isArray(value)) {
      return (value as T[]).includes(optionValue)
    }
    return value === optionValue
  }

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {label && (
        <label className={clsx(
          'block text-sm font-normal mb-2',
          error ? 'text-red-700 dark:text-red-400' : 'text-gray-700 dark:text-gray-300',
          disabled && 'text-gray-400'
        )}>
          {label}
        </label>
      )}

      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={clsx(
          'relative w-full bg-white dark:bg-gray-800 border rounded-md px-3 py-2 text-left',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
          'transition-colors',
          error
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 dark:border-gray-600',
          disabled && 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed opacity-50'
        )}
      >
        <div className="flex items-center justify-between">
          <span className={clsx(
            'block truncate',
            selectedOptions.length === 0 && 'text-gray-500 dark:text-gray-400'
          )}>
            {displayText}
          </span>
          <ChevronDownIcon className={clsx(
            'h-5 w-5 text-gray-400 transition-transform',
            isOpen && 'transform rotate-180'
          )} />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
          {searchable && (
            <div className="p-2 border-b border-gray-200 dark:border-gray-600">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          )}

          <div className="py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                Aucun résultat trouvé
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => !option.disabled && handleOptionSelect(option.value)}
                  disabled={option.disabled}
                  className={clsx(
                    'relative w-full px-3 py-2 text-left text-sm transition-colors',
                    'hover:bg-gray-100 dark:hover:bg-gray-700',
                    isSelected(option.value) && 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400',
                    option.disabled && 'text-gray-400 cursor-not-allowed'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{option.label}</span>
                    {isSelected(option.value) && (
                      <CheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
          {error}
        </div>
      )}
    </div>
  )
}